import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { IApiType } from '../../../types'

const QUERY_NAME = 'listLocationMetricValues'
const query = gql`
  query (
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    ${QUERY_NAME}(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationId
        metricData
      }
    }
  }
`

export const fwWingstopLocationRevenueCenterConfigs = {
  name: 'string',
  sales: 'price',
  percentSales: 'percent',
} as const

const useLocationRevenueCenter = (): IApiType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const iFilter = {
    location_ids: groupFilter?.ids,
    metric_groups: ['Expo Module - Sales By Order Mode'],
  }

  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const nodes = data?.[QUERY_NAME].nodes as
        | {
            locationId: string
            metricData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      if (!nodes) return null
      const mappedData: {
        [revCenter: string]: {
          [kpi: string]: number | string
        }
      } = {}
      const metricData = nodes[0]?.metricData

      let netSales = 0
      for (const key in metricData) {
        if (key === 'sales_total_sales') {
          if (metricData[key].unit === 'CENT') {
            netSales = metricData[key].value / 100
          } else {
            netSales = metricData[key].value
          }
          continue
        }

        const { kpi, revCenter } = (() => {
          const kpiAndRevCenterSales = key.split('sales_')?.[1]
          if (kpiAndRevCenterSales)
            return {
              kpi: 'sales',
              revCenter: kpiAndRevCenterSales,
            }

          return {
            kpi: '',
            revCenter: '',
          }
        })()

        const revCenterSalesValue =
          metricData[key]?.unit === 'CENT'
            ? metricData[key]?.value / 100.0
            : metricData[key]?.value

        if (!mappedData[revCenter]) {
          mappedData[revCenter] = {
            name: (metricData[key]?.name || '').replace('sales for ', ''),
            [kpi]: revCenterSalesValue,
          }
        } else {
          mappedData[revCenter][kpi] = revCenterSalesValue
        }
      }

      const rows = Object.values(mappedData)?.map((n) => {
        return {
          ...n,
          percentSales: (100 * (n.sales as number)) / netSales,
          id: n.name as string,
          parentId: 'root',
        }
      })

      return _.orderBy(rows, ['sales'], ['desc'])
    }, [groupFilter, data]),
    loading,
  }
}

export default useLocationRevenueCenter
